import React from 'react'
import { Link } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { AlertCircle } from 'lucide-react'
import { Lead, SalesFunnel } from '../../types'
import { KanbanColumn } from './KanbanColumn'
import { AlertMessage } from '../ui'

interface LeadsKanbanViewProps {
 funnel: SalesFunnel | null
 leads: Lead[]
 onViewLead?: (lead: Lead) => void
 onEditLead: (lead: Lead) => void
 onUpdateLeadStage: (leadId: string, stageId: string) => Promise<void>
 onAdvanceStageBlocked?: () => void
}

export const LeadsKanbanView: React.FC<LeadsKanbanViewProps> = ({
 funnel,
 leads,
 onViewLead,
 onEditLead,
 onUpdateLeadStage,
 onAdvanceStageBlocked,
}) => {
 const { t } = useTranslation()

 if (!funnel) {
 return (
 <div className="flex flex-col items-center justify-center py-12">
 <AlertCircle className="h-16 w-16 text-muted-foreground mb-4" />
 <h3 className="text-lg font-semibold text-foreground mb-2">
 {t('leads.kanban.funnelNotConfiguredTitle')}
 </h3>
 <p className="text-muted-foreground text-center max-w-md mb-6">
 {t('leads.kanban.funnelNotConfiguredDescription')}
 </p>
 <Link
 to="/leads/settings"
 className="inline-flex items-center justify-center rounded-lg border border-border bg-card px-4 py-2 text-sm font-medium text-accent-600 shadow-sm transition-colors hover:bg-muted focus:outline-none focus:ring-2 focus:ring-accent-500 focus:ring-offset-2 dark:text-accent-400"
 >
 {t('leads.kanban.configureFunnelLink')}
 </Link>
 </div>
 )
 }

 if (!funnel.stages || funnel.stages.length === 0) {
 return (
 <AlertMessage
 variant="warning"
 message="O funil de vendas não possui etapas configuradas. Configure as etapas nas configurações de leads."
 />
 )
 }

 const sortedStages = React.useMemo(
 () => [...funnel.stages].sort((a, b) => a.order - b.order),
 [funnel.stages],
 )

 const getLeadStageIndex = React.useCallback(
 (lead: Lead) => {
 const fid =
 (lead as { funnelStageId?: string }).funnelStageId ||
 (lead as { stageId?: string }).stageId
 if (fid) {
 const idx = sortedStages.findIndex((s) => s.id === fid)
 if (idx >= 0) return idx
 }
 return 0
 },
 [sortedStages],
 )

 const leadsByStage = React.useMemo(() => {
 const grouped: Record<string, Lead[]> = {}
 sortedStages.forEach((stage) => {
 grouped[stage.id!] = []
 })
 leads.forEach((lead) => {
 const stageId =
 (lead as { funnelStageId?: string }).funnelStageId ||
 (lead as { stageId?: string }).stageId ||
 sortedStages[0]?.id
 if (stageId && grouped[stageId]) {
 grouped[stageId].push(lead)
 }
 })
 return grouped
 }, [leads, sortedStages])

 const handleMoveLeadForward = React.useCallback(
 async (lead: Lead) => {
 if (!lead.assignedTo) {
 onAdvanceStageBlocked?.()
 return
 }
 const currentStageIndex = getLeadStageIndex(lead)
 if (currentStageIndex < sortedStages.length - 1) {
 const nextStage = sortedStages[currentStageIndex + 1]
 await onUpdateLeadStage(lead.id, nextStage.id!)
 }
 },
 [getLeadStageIndex, onAdvanceStageBlocked, onUpdateLeadStage, sortedStages],
 )

 const handleMoveLeadBackward = React.useCallback(
 async (lead: Lead) => {
 const currentStageIndex = getLeadStageIndex(lead)
 if (currentStageIndex > 0) {
 const prevStage = sortedStages[currentStageIndex - 1]
 await onUpdateLeadStage(lead.id, prevStage.id!)
 }
 },
 [getLeadStageIndex, onUpdateLeadStage, sortedStages],
 )

 return (
 <div className="overflow-x-auto pb-4">
 <div className="flex gap-4 min-w-min">
 {sortedStages.map((stage, index) => (
 <KanbanColumn
 key={stage.id}
 stage={stage}
 stageIndex={index}
 leads={leadsByStage[stage.id!] || []}
 isFirst={index === 0}
 isLast={index === sortedStages.length - 1}
 onViewLead={onViewLead}
 onEditLead={onEditLead}
 onMoveLeadForward={handleMoveLeadForward}
 onMoveLeadBackward={handleMoveLeadBackward}
 />
 ))}
 </div>
 </div>
 )
}

